import React, { useContext, useEffect, useState } from 'react';
import { useMutation, useQuery } from '@apollo/react-hooks';
import { MenuItem, Select } from '@material-ui/core';
import { Button, Card, Divider, Form, InputNumber, Typography } from 'antd';
import { Delete, Visibility, VisibilityOff } from '@material-ui/icons';
import { Formik } from 'formik';
import * as Yup from 'yup';
import moment from 'moment';
import styled from 'styled-components';
import { displayFlex } from '../../../styles/Mixins';
import { paragraphReg } from '../../../styles/Typography';
import colors from '../../../styles/Colors';
import ContextSnackbar from 'Components/Snackbar';
import { HeadingTwo } from 'Components/Headings';
import { EVENT_PRODUCTS, VENUES } from 'Queries/SuperAdmin';
import { SnackbarContext } from 'Store/SnackbarContext';
import { CREATE_STALL_PRODUCT_DISCOUNT } from 'Mutations/CreateStallProductDiscount';
import { CREATE_RV_PRODUCT_DISCOUNT } from 'Mutations/CreateRVProductDiscount';
import { DELETE_STALL_PRODUCT_DISCOUNT } from 'Mutations/DeleteStallProductDiscount';
import { DELETE_RV_PRODUCT_DISCOUNT } from 'Mutations/DeleteRVProductDiscount';
import { HIDE_STALL_PRODUCT } from 'Mutations/HideStallProduct';
import { HIDE_RV_PRODUCT } from 'Mutations/HideRVProduct';
import Calendar from 'Components/Calendar/Calendar';
import WarningModal from 'Components/WarningModal/WarningModal';
import productRefType from '../../../constants/productRefType';

const { Title, Text } = Typography;

const getOptions = data => {
  if (!data) return null;

  return data.map(data => (
    <MenuItem key={data.id} value={data.id}>
      ID: {data.id} {data.name}
    </MenuItem>
  ));
};

const ProductCard = styled(Card)`
  margin: 8px;
  width: 200px;
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
    transform: translateY(-2px);
  }

  &.selected {
    border: 2px solid ${colors.primary};
    background-color: ${colors.primary};
  }
`;

const ProductsContainer = styled.div`
  ${displayFlex};
  flex-direction: column;
  gap: 20px;
`;

const ProductsSection = styled.div`
  ${displayFlex};
  flex-direction: column;
  gap: 10px;
`;

const ProductsGrid = styled.div`
  ${displayFlex};
  flex-direction: row;
  flex-wrap: wrap;
  gap: 10px;
  max-height: 300px;
  overflow-y: auto;
  padding: 10px;
  border: 1px solid #d9d9d9;
  border-radius: 6px;
`;

const ProductDetailsSection = styled.div`
  margin-top: 20px;
  padding: 20px;
  background-color: #f9f9f9;
  border-radius: 6px;
`;

const DiscountFormSection = styled.div`
  margin-top: 20px;
  padding: 20px;
  background-color: #ffffff;
  border: 1px solid #d9d9d9;
  border-radius: 6px;
`;

const ExplanationSection = styled.div`
  margin-bottom: 20px;
  padding: 15px;
  background-color: #f0f8ff;
  border: 1px solid #d1e7dd;
  border-radius: 6px;

  h4 {
    margin-bottom: 10px;
    color: #0c5460;
  }

  ul {
    margin: 0;
    padding-left: 20px;

    li {
      margin-bottom: 8px;
      color: #0f5132;
    }
  }
`;

const DiscountsSection = styled.div`
  margin-top: 20px;
  padding: 20px;
  background-color: #f6f8fa;
  border: 1px solid #e1e4e8;
  border-radius: 6px;

  h4 {
    margin-bottom: 15px;
    color: #24292e;
  }

  .discount-item {
    margin-bottom: 15px;
    padding: 15px;
    background-color: #ffffff;
    border: 1px solid #d1d5da;
    border-radius: 6px;

    .discount-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: 10px;

      .discount-actions {
        display: flex;
        align-items: center;
        gap: 10px;
      }
    }

    .discount-details {
      display: flex;
      flex-direction: column;
      gap: 5px;
    }

    .discount-badge {
      padding: 4px 8px;
      background-color: #28a745;
      color: white;
      border-radius: 4px;
      font-size: 12px;
      font-weight: bold;
    }

    .discount-value {
      font-weight: bold;
      color: #28a745;
    }

    .discount-dates {
      color: #586069;
      font-size: 14px;
    }

    .delete-button {
      background: none;
      border: none;
      color: #d73a49;
      cursor: pointer;
      padding: 5px;
      border-radius: 4px;
      display: flex;
      align-items: center;
      transition: background-color 0.2s;

      &:hover {
        background-color: #f8d7da;
      }

      &:disabled {
        opacity: 0.5;
        cursor: not-allowed;
      }
    }
  }

  .no-discounts {
    text-align: center;
    color: #586069;
    font-style: italic;
  }
`;

const SuperAdminEventProducts = styled(EventProducts)`
  &&& {
    ${displayFlex}
    align-items: center;
    width: 100%;
    height: auto;
    padding: 12px 0 11px 20px;
    ${paragraphReg}
    font-size: 14px;
    text-align: left;
    background-color: ${colors.background.primary};
    color: ${colors.text};
    border-radius: 3px;
  }

  &__page-header {
    text-align: left;
    width: 100%;
    border-bottom: 1px solid rgb(200, 214, 229);
    &&& {
      margin: 65px 0 25px;
    }
  }

  &__page-body {
    text-align: left;
    width: 100%;
    &&& {
      margin: 0;
      padding: 10px;
    }

    h3 {
      text-transform: uppercase;
      font-family: IBMPlexSans-Bold;
      font-size: 12px;
      font-weight: 400;
      color: rgb(79, 93, 109);
      margin-top: 0px;
      margin-bottom: 3px;
    }
  }

  &__selector-container {
    display: flex;
    flex-direction: column;
    gap: 20px;
  }

  &__selector-item {
    margin-bottom: 10px;
  }

  &__page-body-child {
    margin-top: 20px;
  }

  &__header-wrapper {
    margin: 20px 0px !important;
    display: flex;
    justify-content: space-between;
    align-items: center;
  }

  .MuiInputBase-root {
    border: ${colors.text.lightGray2} 1px solid;
    border-radius: 3px;
    padding: 0 10px;
    min-width: 300px;
  }

  .MuiSvgIcon-root {
    width: 0.8em;
    height: 0.8em;
  }

  .MuiIconButton-root {
    padding-top: 6px;
  }

  .line {
    background: ${colors.border.primary};
    height: 1px;
    flex: 1;
    position: relative;
    top: 11px;
  }
`;

const DiscountSchema = Yup.object().shape({
  startDate: Yup.date().required('Start date is required'),
  endDate: Yup.date()
    .required('End date is required')
    .min(Yup.ref('startDate'), 'End date must be after start date'),
  rate: Yup.number()
    .required('Rate is required')
    .min(0, 'Rate must be positive'),
  maxQuantity: Yup.number().min(1, 'Max quantity must be at least 1')
});

function EventProducts(props) {
  const { className } = props;
  const { showSnackbar } = useContext(SnackbarContext);
  const [venues, setVenues] = useState([]);
  const [selectedVenue, setSelectedVenue] = useState(null);
  const [events, setEvents] = useState([]);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [stallProducts, setStallProducts] = useState([]);
  const [rvProducts, setRvProducts] = useState([]);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [selectedProductType, setSelectedProductType] = useState(null);
  const [showDiscountForm, setShowDiscountForm] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [discountToDelete, setDiscountToDelete] = useState(null);
  const [hideModalOpen, setHideModalOpen] = useState(false);
  const [productToHide, setProductToHide] = useState(null);

  const { data: venuesData, loading: venuesLoading, error: venuesError } = useQuery(VENUES, {
    fetchPolicy: 'network-only'
  });

  const { data: eventProductsData, refetch: refetchEventProducts } = useQuery(EVENT_PRODUCTS, {
    variables: { id: selectedEvent?.id },
    fetchPolicy: 'network-only',
    skip: !selectedEvent
  });

  const [createStallProductDiscount, { loading: createStallDiscountLoading }] = useMutation(CREATE_STALL_PRODUCT_DISCOUNT, {
    onCompleted: data => {
      if (data.createStallProductDiscount.error) {
        showSnackbar(`Error creating discount: ${data.createStallProductDiscount.error}`, {
          duration: 5000,
          error: true
        });
      } else {
        showSnackbar('Stall product discount created successfully', {
          duration: 3000,
          error: false
        });
        setShowDiscountForm(false);
        refetchEventProducts();
      }
    },
    onError: error => {
      console.error('Error creating stall product discount:', error);
      showSnackbar('Error creating stall product discount', {
        duration: 5000,
        error: true
      });
    }
  });

  const [createRVProductDiscount, { loading: createRVDiscountLoading }] = useMutation(CREATE_RV_PRODUCT_DISCOUNT, {
    onCompleted: data => {
      if (data.createRVProductDiscount.error) {
        showSnackbar(`Error creating discount: ${data.createRVProductDiscount.error}`, {
          duration: 5000,
          error: true
        });
      } else {
        showSnackbar('RV product discount created successfully', {
          duration: 3000,
          error: false
        });
        setShowDiscountForm(false);
        refetchEventProducts();
      }
    },
    onError: error => {
      console.error('Error creating RV product discount:', error);
      showSnackbar('Error creating RV product discount', {
        duration: 5000,
        error: true
      });
    }
  });

  const [deleteStallProductDiscount, { loading: deleteStallDiscountLoading }] = useMutation(DELETE_STALL_PRODUCT_DISCOUNT, {
    onCompleted: data => {
      if (data.deleteStallProductDiscount.error) {
        showSnackbar(`Error deleting discount: ${data.deleteStallProductDiscount.error}`, {
          duration: 5000,
          error: true
        });
      } else {
        showSnackbar('Stall product discount deleted successfully', {
          duration: 3000,
          error: false
        });
        refetchEventProducts();
      }
    },
    onError: error => {
      console.error('Error deleting stall product discount:', error);
      showSnackbar('Error deleting stall product discount', {
        duration: 5000,
        error: true
      });
    }
  });

  const [deleteRVProductDiscount, { loading: deleteRVDiscountLoading }] = useMutation(DELETE_RV_PRODUCT_DISCOUNT, {
    onCompleted: data => {
      if (data.deleteRVProductDiscount.error) {
        showSnackbar(`Error deleting discount: ${data.deleteRVProductDiscount.error}`, {
          duration: 5000,
          error: true
        });
      } else {
        showSnackbar('RV product discount deleted successfully', {
          duration: 3000,
          error: false
        });
        refetchEventProducts();
      }
    },
    onError: error => {
      console.error('Error deleting RV product discount:', error);
      showSnackbar('Error deleting RV product discount', {
        duration: 5000,
        error: true
      });
    }
  });

  const [hideStallProduct, { loading: hideStallProductLoading }] = useMutation(HIDE_STALL_PRODUCT, {
    onCompleted: data => {
      if (data.hideStallProduct.error) {
        showSnackbar(`Error updating product visibility: ${data.hideStallProduct.error}`, {
          duration: 5000,
          error: true
        });
      } else {
        const action = productToHide?.hidden ? 'shown' : 'hidden';
        showSnackbar(`Stall product ${action} successfully`, {
          duration: 3000,
          error: false
        });
        refetchEventProducts();
      }
    },
    onError: error => {
      console.error('Error updating product visibility:', error);
      showSnackbar('Error updating product visibility', {
        duration: 5000,
        error: true
      });
    }
  });

  const [hideRVProduct, { loading: hideRVProductLoading }] = useMutation(HIDE_RV_PRODUCT, {
    onCompleted: data => {
      if (data.hideRVProduct.error) {
        showSnackbar(`Error updating product visibility: ${data.hideRVProduct.error}`, {
          duration: 5000,
          error: true
        });
      } else {
        const action = productToHide?.hidden ? 'shown' : 'hidden';
        showSnackbar(`RV product ${action} successfully`, {
          duration: 3000,
          error: false
        });
        refetchEventProducts();
      }
    },
    onError: error => {
      console.error('Error updating RV product visibility:', error);
      showSnackbar('Error updating product visibility', {
        duration: 5000,
        error: true
      });
    }
  });

  useEffect(() => {
    if (venuesData) {
      const sortedVenues = venuesData.venues.sort((a, b) => a.id - b.id);
      setVenues(sortedVenues);
    }
  }, [venuesData]);

  useEffect(() => {
    if (selectedVenue && selectedVenue.events) {
      const sortedEvents = selectedVenue.events.sort((a, b) => b.id - a.id);
      setEvents(sortedEvents);
      setSelectedEvent(null);
      setSelectedProduct(null);
      setSelectedProductType(null);
      setShowDiscountForm(false);
    } else {
      setEvents([]);
      setSelectedEvent(null);
      setSelectedProduct(null);
      setSelectedProductType(null);
      setShowDiscountForm(false);
    }
  }, [selectedVenue]);

  useEffect(() => {
    if (eventProductsData && eventProductsData.event) {
      setStallProducts(eventProductsData.event.stallProducts || []);
      setRvProducts(eventProductsData.event.rvProducts || []);
      setSelectedProduct(null);
      setSelectedProductType(null);
      setShowDiscountForm(false);
    }
  }, [eventProductsData]);

  const handleVenueChange = e => {
    const venue = venues.filter(venue => venue.id === e.target.value)[0];
    setSelectedVenue(venue);
  };

  const handleEventChange = e => {
    const event = events.filter(event => event.id === e.target.value)[0];
    setSelectedEvent(event);
  };

  const handleProductClick = (product, type) => {
    setSelectedProduct(product);
    setSelectedProductType(type);
    setShowDiscountForm(false);
  };

  const handleAddDiscount = () => {
    setShowDiscountForm(true);
  };

  const handleDeleteDiscount = discount => {
    setDiscountToDelete(discount);
    setDeleteModalOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!discountToDelete) return;

    try {
      if (selectedProductType === 'stall') {
        await deleteStallProductDiscount({
          variables: { discountId: discountToDelete.id }
        });
      } else if (selectedProductType === 'rv') {
        await deleteRVProductDiscount({
          variables: { discountId: discountToDelete.id }
        });
      }
    } catch (error) {
      console.error('Error deleting discount:', error);
    } finally {
      setDeleteModalOpen(false);
      setDiscountToDelete(null);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteModalOpen(false);
    setDiscountToDelete(null);
  };

  const handleHideProduct = product => {
    setProductToHide(product);
    setHideModalOpen(true);
  };

  const handleHideConfirm = async () => {
    if (!productToHide) return;

    try {
      if (selectedProductType === 'stall') {
        await hideStallProduct({
          variables: {
            stallProductId: productToHide.id,
            hidden: !productToHide.hidden
          }
        });
      } else if (selectedProductType === 'rv') {
        await hideRVProduct({
          variables: {
            rvProductId: productToHide.id,
            hidden: !productToHide.hidden
          }
        });
      }
    } catch (error) {
      console.error('Error updating product visibility:', error);
    } finally {
      setHideModalOpen(false);
      setProductToHide(null);
    }
  };

  const handleHideCancel = () => {
    setHideModalOpen(false);
    setProductToHide(null);
  };

  const handleDiscountSubmit = async values => {
    try {
      const input = {
        xProductId: parseInt(selectedProduct.id),
        xRefTypeId: selectedProductType === 'stall' ? productRefType.STALL_PRODUCT : productRefType.RV_PRODUCT,
        startDate: values.startDate ? moment(values.startDate).format('YYYY-MM-DD') : null,
        endDate: values.endDate ? moment(values.endDate).format('YYYY-MM-DD') : null,
        rate: parseFloat(values.rate),
        maxQuantity: values.maxQuantity ? parseInt(values.maxQuantity) : null
      };

      if (selectedProductType === 'stall') {
        await createStallProductDiscount({
          variables: { input }
        });
      } else if (selectedProductType === 'rv') {
        await createRVProductDiscount({
          variables: { input }
        });
      }
    } catch (error) {
      console.error('Error creating discount:', error);
      showSnackbar('Error creating discount', {
        duration: 5000,
        error: true
      });
    }
  };

  const renderProductCard = (product, type) => (
    <ProductCard
      key={`${type}-${product.id}`}
      size="small"
      className={selectedProduct?.id === product.id && selectedProductType === type ? 'selected' : ''}
      onClick={() => handleProductClick(product, type)}>
      <Title level={5}>{product.name}</Title>
      <Text type="secondary">${product.price}</Text>
    </ProductCard>
  );

  const renderExistingDiscounts = () => {
    if (!selectedProduct || !selectedProduct.discounts) return null;

    const getDiscountDisplayText = rate => {
      return parseFloat(rate) === 0 ? 'FREE' : `$${rate} off`;
    };

    return (
      <DiscountsSection>
        <Title level={4}>Existing Discounts</Title>
        {selectedProduct.discounts.length > 0 ? (
          selectedProduct.discounts.map(discount => (
            <div key={discount.id} className="discount-item">
              <div className="discount-header">
                <span className="discount-badge">Active Discount</span>
                <div className="discount-actions">
                  <Text className="discount-value">{getDiscountDisplayText(discount.rate)}</Text>
                  <button
                    className="delete-button"
                    onClick={() => handleDeleteDiscount(discount)}
                    disabled={deleteStallDiscountLoading || deleteRVDiscountLoading}
                    title="Delete discount">
                    <Delete fontSize="small" />
                  </button>
                </div>
              </div>
              <div className="discount-details">
                <Text className="discount-dates">
                  <strong>Valid Period:</strong> {moment(discount.startDate).format('MMM DD, YYYY')} - {moment(discount.endDate).format('MMM DD, YYYY')}
                </Text>
                {discount.maxQuantity && (
                  <Text>
                    <strong>Max Quantity:</strong> {discount.maxQuantity} units
                  </Text>
                )}
                <Text style={{ fontSize: '12px', color: '#586069' }}>Created: {moment(discount.createdAt).format('MMM DD, YYYY')}</Text>
              </div>
            </div>
          ))
        ) : (
          <div className="no-discounts">No discounts available for this product</div>
        )}
      </DiscountsSection>
    );
  };

  const renderProductDetails = () => {
    if (!selectedProduct) return null;

    return (
      <ProductDetailsSection>
        <Title level={3}>
          {selectedProduct.name} (ID: {selectedProduct.id})
        </Title>
        <div style={{ marginBottom: '10px' }}>
          <Text strong>Price: </Text>
          <Text>${selectedProduct.price}</Text>
        </div>
        {selectedProduct.description && (
          <div style={{ marginBottom: '10px' }}>
            <Text strong>Description: </Text>
            <Text>{selectedProduct.description}</Text>
          </div>
        )}
        <div style={{ marginBottom: '10px' }}>
          <Text strong>Start Date: </Text>
          <Text>{selectedProduct.startDate}</Text>
        </div>
        <div style={{ marginBottom: '10px' }}>
          <Text strong>End Date: </Text>
          <Text>{selectedProduct.endDate}</Text>
        </div>
        <div style={{ marginBottom: '10px' }}>
          <Text strong>Nightly: </Text>
          <Text>{selectedProduct.nightly ? 'Yes' : 'No'}</Text>
        </div>

        <div style={{ marginBottom: '15px' }}>
          <Text strong>Visibility: </Text>
          <Text style={{ color: selectedProduct.hidden ? '#d73a49' : '#28a745', fontWeight: 'bold' }}>
            {selectedProduct.hidden ? 'Hidden from renters' : 'Visible to renters'}
          </Text>
          <Button
            type={selectedProduct.hidden ? 'default' : 'danger'}
            onClick={() => handleHideProduct(selectedProduct)}
            loading={selectedProductType === 'stall' ? hideStallProductLoading : hideRVProductLoading}
            style={{ marginLeft: '10px' }}
            icon={selectedProduct.hidden ? <Visibility /> : <VisibilityOff />}>
            {selectedProduct.hidden ? 'Show to Renters' : 'Hide from Renters'}
          </Button>
        </div>

        <Button type="primary" onClick={handleAddDiscount}>
          + Add Discount
        </Button>
      </ProductDetailsSection>
    );
  };

  const renderDiscountForm = () => {
    if (!showDiscountForm) return null;

    return (
      <DiscountFormSection>
        <Title level={4}>Create Discount</Title>

        <ExplanationSection>
          <Title level={5}>Important Information</Title>
          <ul>
            <li>
              <strong>Rate:</strong> This is a fixed dollar amount to be discounted from the product price. If the rate is $0, the product becomes free for the
              selected dates.
            </li>
            <li>
              <strong>Dates:</strong> These dates refer to the renter's stay period (reservation dates), not the booking dates.
            </li>
            <li>
              <strong>Max Quantity:</strong> This refers to the maximum number of units that can have this discount applied. This field is optional.
            </li>
          </ul>
        </ExplanationSection>

        <Formik
          initialValues={{
            startDate: null,
            endDate: null,
            rate: '',
            maxQuantity: ''
          }}
          validationSchema={DiscountSchema}
          onSubmit={handleDiscountSubmit}>
          {({ values, setFieldValue, errors, touched, handleSubmit, isSubmitting }) => {
            const handleDateChange = dateRange => {
              if (dateRange && dateRange.startDate && dateRange.endDate) {
                setFieldValue('startDate', dateRange.startDate);
                setFieldValue('endDate', dateRange.endDate);
              }
            };

            return (
              <Form onFinish={handleSubmit}>
                <div style={{ marginBottom: '15px' }}>
                  <label>Select Date Range:</label>
                  <div style={{ marginTop: '5px' }}>
                    <Calendar
                      plural={true}
                      selectableRangeStart={selectedProduct.startDate}
                      selectableRangeEnd={selectedProduct.endDate}
                      cb={handleDateChange}
                      placeholder={['Start Date', 'End Date']}
                      allowClear={true}
                    />
                  </div>
                  {((errors.startDate && touched.startDate) || (errors.endDate && touched.endDate)) && (
                    <div style={{ color: 'red', fontSize: '12px' }}>{errors.startDate || errors.endDate}</div>
                  )}
                </div>

                <div style={{ marginBottom: '15px' }}>
                  <label>Rate ($):</label>
                  <InputNumber
                    value={values.rate}
                    onChange={value => setFieldValue('rate', value)}
                    min={0}
                    style={{ width: '100%', marginTop: '5px' }}
                    formatter={value => `$ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                    parser={value => value.replace(/\$\s?|(,*)/g, '')}
                  />
                  {errors.rate && touched.rate && <div style={{ color: 'red', fontSize: '12px' }}>{errors.rate}</div>}
                </div>

                <div style={{ marginBottom: '15px' }}>
                  <label>Max Quantity (optional):</label>
                  <InputNumber
                    value={values.maxQuantity}
                    onChange={value => setFieldValue('maxQuantity', value)}
                    min={1}
                    style={{ width: '100%', marginTop: '5px' }}
                  />
                  {errors.maxQuantity && touched.maxQuantity && <div style={{ color: 'red', fontSize: '12px' }}>{errors.maxQuantity}</div>}
                </div>

                <div style={{ display: 'flex', gap: '10px' }}>
                  <Button type="primary" htmlType="submit" loading={isSubmitting || createStallDiscountLoading || createRVDiscountLoading}>
                    Create Discount
                  </Button>
                  <Button onClick={() => setShowDiscountForm(false)}>Cancel</Button>
                </div>
              </Form>
            );
          }}
        </Formik>
      </DiscountFormSection>
    );
  };

  return (
    <>
      <div className={className}>
        <ContextSnackbar />
        <div className={`${className}__page-header`}>
          <div className={`${className}__header-wrapper`}>
            <HeadingTwo label="EVENT PRODUCTS" />
          </div>
        </div>
      </div>

      <WarningModal
        isOpen={deleteModalOpen}
        handleClose={handleDeleteCancel}
        onCancel={handleDeleteCancel}
        onContinue={handleDeleteConfirm}
        header="Delete Discount"
        text={`Are you sure you want to delete this discount? This action cannot be undone.`}
        continueLabel="DELETE"
        cancelLabel="CANCEL"
        hideWarningIcon={false}
      />

      <WarningModal
        isOpen={hideModalOpen}
        handleClose={handleHideCancel}
        onCancel={handleHideCancel}
        onContinue={handleHideConfirm}
        header={productToHide?.hidden ? 'Show Product to Renters' : 'Hide Product from Renters'}
        text={
          productToHide?.hidden
            ? 'This product will be made visible to renters and they will be able to book it. This action can be changed at any time.'
            : 'This product will be hidden from renters and they will not be able to see or book it. The product will remain visible to admins only. This action can be changed at any time.'
        }
        continueLabel={productToHide?.hidden ? 'SHOW' : 'HIDE'}
        cancelLabel="CANCEL"
        hideWarningIcon={true}
      />
      <div className={className}>
        <div className={`${className}__page-body`}>
          <div className={`${className}__selector-container`}>
            <div className={`${className}__selector-item`}>
              <h3>Choose a venue</h3>
              {venues && !venuesLoading && !venuesError && (
                <Select
                  data-testid="venue-select"
                  onChange={handleVenueChange}
                  value={selectedVenue?.id || ''}
                  disableUnderline
                  style={{ width: '100%', marginBottom: '20px' }}>
                  {getOptions(venues)}
                </Select>
              )}
            </div>

            <div className={`${className}__selector-item`}>
              <h3>Choose an event</h3>
              <Select
                data-testid="event-select"
                onChange={handleEventChange}
                value={selectedEvent?.id || ''}
                disableUnderline
                style={{ width: '100%', marginBottom: '20px' }}
                disabled={!selectedVenue || events.length === 0}>
                {getOptions(events)}
              </Select>
            </div>

            {selectedEvent && (
              <ProductsContainer>
                <ProductsSection>
                  <Title level={3}>Stall Products</Title>
                  <ProductsGrid>
                    {stallProducts.length > 0 ? stallProducts.map(product => renderProductCard(product, 'stall')) : <Text>No stall products available</Text>}
                  </ProductsGrid>
                </ProductsSection>

                <ProductsSection>
                  <Title level={3}>RV Products</Title>
                  <ProductsGrid>
                    {rvProducts.length > 0 ? rvProducts.map(product => renderProductCard(product, 'rv')) : <Text>No RV products available</Text>}
                  </ProductsGrid>
                </ProductsSection>

                {selectedProduct && (
                  <>
                    <Divider />
                    {renderProductDetails()}
                    {renderExistingDiscounts()}
                    {renderDiscountForm()}
                  </>
                )}
              </ProductsContainer>
            )}
          </div>
        </div>
      </div>
    </>
  );
}

export default SuperAdminEventProducts;
